/**
 * CWS-2053: the plate typed for this customer is on another customer's car.
 *
 * The server moves the car on its own when that holder has no details at all. When the
 * holder is a real customer it refuses with VEHICLE_TRANSFER_CONFIRM and the attendant
 * picks: keep the car with them, or transfer it here (resend with `transfer_vehicle_id`).
 * A car carrying an active membership is refused with VEHICLE_TRANSFER_BLOCKED instead.
 */

export const VEHICLE_TRANSFER_CONFIRM = 'VEHICLE_TRANSFER_CONFIRM';
export const VEHICLE_TRANSFER_BLOCKED = 'VEHICLE_TRANSFER_BLOCKED';

export interface VehicleTransferPrompt {
  /** The car that holds the plate now; sent back as `transfer_vehicle_id` to confirm. */
  vehicleId: string;
  holderName: string;
  title: string;
  message: string;
  keepLabel: string;
  transferLabel: string;
}

interface TransferErrorData {
  code?: string;
  holder?: { name?: string | null; contact_last4?: string | null };
  vehicle?: {
    id?: string;
    license_plate?: string | null;
    make?: string | null;
    model?: string | null;
    year?: number | string | null;
  };
}

function errorData(error: unknown): TransferErrorData | null {
  const body = (error as { error?: { data?: unknown } } | null)?.error;
  const data = body?.data;
  return data && typeof data === 'object' ? (data as TransferErrorData) : null;
}

function present(value: unknown): string {
  return value === undefined || value === null ? '' : String(value).trim();
}

/** "Jane Doe (···1234)", or a neutral phrase when the holder has no name on file. */
export function vehicleHolderLabel(holder: TransferErrorData['holder']): string {
  const name = present(holder?.name) || 'another customer';
  const last4 = present(holder?.contact_last4);
  return last4 ? `${name} (···${last4})` : name;
}

function vehicleLabel(vehicle: TransferErrorData['vehicle']): string {
  const description = [vehicle?.year, vehicle?.make, vehicle?.model].map(present).filter(Boolean);
  const plate = present(vehicle?.license_plate);
  if (description.length && plate) return `${description.join(' ')} (${plate})`;
  return description.join(' ') || plate || 'This vehicle';
}

/** The confirm dialog for a 409 VEHICLE_TRANSFER_CONFIRM, or null for any other error. */
export function readVehicleTransferPrompt(error: unknown): VehicleTransferPrompt | null {
  const data = errorData(error);
  const vehicleId = present(data?.vehicle?.id);
  if (data?.code !== VEHICLE_TRANSFER_CONFIRM || !vehicleId) return null;

  const holderName = vehicleHolderLabel(data.holder);
  return {
    vehicleId,
    holderName,
    title: 'Vehicle belongs to another customer',
    message:
      `${vehicleLabel(data.vehicle)} is registered to ${holderName}. ` +
      'Leave it with that customer, or transfer it to this customer?',
    keepLabel: 'Leave with current customer',
    transferLabel: 'Transfer vehicle',
  };
}

/** Screens without the dialog say where the transfer is done instead of asking. */
export function vehicleTransferElsewhereMessage(prompt: VehicleTransferPrompt): string {
  return (
    `This license plate is registered to ${prompt.holderName}. ` +
    'To move the vehicle, add it from the customer’s Vehicles tab in Customer Details.'
  );
}

/** Toast title for a refused save: each refusal has its own name (CWS-1398, CWS-2053). */
export function vehicleSaveErrorSummary(error: unknown): string {
  switch (errorData(error)?.code) {
    case 'VEHICLE_PLATE_TAKEN':
      return 'Plate Already Registered';
    case VEHICLE_TRANSFER_BLOCKED:
      return 'Vehicle Has an Active Membership';
    default:
      return 'Error';
  }
}
